/**
 * Auto Invoicing Tab Component - Complete Invoicing System
 * Features:
 * 1. Create Invoice - Create invoices for children/families
 * 2. Send to Parent - Send invoice to parent with CC to director
 * 3. Auto Monthly - Set up recurring monthly invoices
 * 4. Director Summary - Monthly summary report for director
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subMonths, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Mail, 
  Calendar as CalendarIcon, 
  Settings, 
  Send, 
  Eye, 
  Plus, 
  X, 
  Clock,
  FileText,
  DollarSign,
  Users,
  AlertCircle,
  CheckCircle,
  History,
  User,
  Receipt,
  RefreshCw,
  Repeat,
  Download,
  Printer,
  Check,
  RotateCcw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutoInvoicingTab = ({ user, organization }) => {
  // State for children/families
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loadingChildren, setLoadingChildren] = useState(true);
  
  // State for invoice creation
  const [invoiceData, setInvoiceData] = useState({
    childId: '',
    childName: '',
    parentEmail: '',
    amount: '',
    description: '',
    dueDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
    items: [{ description: 'Monthly Tuition', amount: '' }],
    applyTax: false,
    taxRate: 6,  // Default tax rate percentage
    // Discount fields
    applyDiscount: false,
    discountType: 'percentage', // 'percentage', 'fixed', or 'both'
    discountPercent: 0,
    discountAmount: 0,
    discountReason: ''
  });
  
  // State for auto-monthly settings
  const [autoSettings, setAutoSettings] = useState({
    enabled: false,
    dayOfMonth: 1,
    defaultAmount: '',
    defaultDescription: 'Monthly Childcare Tuition',
    ccDirector: true,
    directorEmail: user?.email || '',
    applyTax: false,
    taxRate: 6,
    zelleEmail: '',
    zellePhone: ''
  });
  
  // State for director summary
  const [summarySettings, setSummarySettings] = useState({
    recipientEmail: user?.email || '',
    ccEmails: [],
    includeTotalInvoices: true,
    includeTotalBilled: true,
    includeBreakdownByFamily: true,
    includeOutstandingBalances: true,
    includePaymentSummary: true
  });
  
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
  });
  
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewStats, setPreviewStats] = useState(null);
  const [activeTab, setActiveTab] = useState('bulk');
  const [history, setHistory] = useState([]);
  const [reportViewer, setReportViewer] = useState({ open: false, html: '' });
  const [newCcEmail, setNewCcEmail] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // New state for Send Monthly Now feature
  const [monthlyCategory, setMonthlyCategory] = useState('all');
  const [categoryChildren, setCategoryChildren] = useState([]);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [loadingCategoryChildren, setLoadingCategoryChildren] = useState(false);
  const [editingBillingId, setEditingBillingId] = useState(null);
  const [billingForm, setBillingForm] = useState({ billingContactName: '', billingContactEmail: '', billingContactMobile: '', textBillingContact: false });
  const [monthlyInvoiceDate, setMonthlyInvoiceDate] = useState(format(addMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [monthlyBillingMonth, setMonthlyBillingMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [sendingMonthly, setSendingMonthly] = useState(false);
  const [monthlyInvoices, setMonthlyInvoices] = useState([]);
  const [monthlyProgress, setMonthlyProgress] = useState({ current: 0, total: 0 });

  // Square connection state
  const [squareStatus, setSquareStatus] = useState({
    connected: false,
    merchant_id: null,
    business_name: null,
    connected_at: null
  });
  const [connectingSquare, setConnectingSquare] = useState(false);

  useEffect(() => {
    fetchChildren();
    fetchHistory();
    fetchAutoSettings();
    fetchMonthlyInvoices();
    fetchSquareStatus();
  }, []);

  // Fetch children when category changes
  useEffect(() => {
    fetchChildrenByCategory(monthlyCategory);
  }, [monthlyCategory]);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children?active_only=true`);
      setChildren(response.data);
    } catch (error) {
      console.error('Error fetching children:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  const fetchHistory = async () => {
    try {
      // Fetch both summary history and sent invoices
      const [summaryRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/auto-invoicing/history`),
        axios.get(`${API}/auto-invoicing/sent-invoices`)
      ]);
      setHistory(summaryRes.data.history || []);
      setSentInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const [sentInvoices, setSentInvoices] = useState([]);

  const fetchAutoSettings = async () => {
    try {
      const response = await axios.get(`${API}/auto-invoicing/settings`);
      if (response.data) {
        setAutoSettings(prev => ({
          ...prev,
          ...response.data,
          directorEmail: response.data.ownerEmail || user?.email || ''
        }));
        setSummarySettings(prev => ({
          ...prev,
          recipientEmail: response.data.ownerEmail || user?.email || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchChildrenByCategory = async (category) => {
    setLoadingCategoryChildren(true);
    try {
      const response = await axios.get(`${API}/auto-invoicing/children-by-category?category=${category}`);
      setCategoryChildren(response.data.children || []);
      // Auto-select all children with emails by default
      setSelectedChildIds(
        response.data.children
          .filter(c => c.hasBillingEmail)
          .map(c => c.id)
      );
    } catch (error) {
      console.error('Error fetching children by category:', error);
    } finally {
      setLoadingCategoryChildren(false);
    }
  };

  const openBillingEditor = (child) => {
    setEditingBillingId(child.id);
    setBillingForm({
      billingContactName: child.billingContactName || '',
      billingContactEmail: child.billingContactEmail || '',
      billingContactMobile: child.billingContactMobile || '',
      textBillingContact: !!child.textBillingContact,
    });
  };

  const saveBillingContact = async (childId) => {
    const emailVal = (billingForm.billingContactEmail || '').trim();
    if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      await axios.put(`${API}/auto-invoicing/children/${childId}/billing-contact`, billingForm);
      toast.success('Billing contact saved');
      setEditingBillingId(null);
      fetchChildrenByCategory(monthlyCategory);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing contact');
    }
  };

  const fetchMonthlyInvoices = async () => {
    try {
      const response = await axios.get(`${API}/auto-invoicing/monthly-invoices`);
      setMonthlyInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching monthly invoices:', error);
    }
  };

  // Square connection functions
  const fetchSquareStatus = async () => {
    try {
      const response = await axios.get(`${API}/square/status`);
      setSquareStatus(response.data);
    } catch (error) {
      console.error('Error fetching Square status:', error);
    }
  };

  const handleConnectSquare = async () => {
    setConnectingSquare(true);
    try {
      const response = await axios.get(`${API}/square/authorize`);
      // Redirect to Square OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error starting Square OAuth:', error);
      toast.error('Failed to connect to Square');
      setConnectingSquare(false);
    }
  };

  const handleDisconnectSquare = async () => {
    if (!window.confirm('Disconnect Square? Parents will no longer be able to pay with card until reconnected.')) {
      return;
    }

    try {
      await axios.post(`${API}/square/disconnect`);
      setSquareStatus({
        connected: false,
        merchant_id: null,
        business_name: null,
        connected_at: null
      });
      toast.success('Square disconnected');
    } catch (error) {
      console.error('Error disconnecting Square:', error);
      toast.error('Failed to disconnect Square');
    }
  };

  const handleSendMonthlyNow = async () => {
    if (selectedChildIds.length === 0) {
      toast.error('Please select at least one child');
      return;
    }

    if (calculateTotal() === 0) {
      toast.error('Please add at least one invoice item with an amount');
      return;
    }

    setSendingMonthly(true);
    setMonthlyProgress({ current: 0, total: selectedChildIds.length });
    
    try {
      // Calculate totals
      const subtotal = calculateSubtotal();
      const discount = calculateDiscount();
      const taxAmount = calculateTax();
      const total = calculateTotal();
      
      // Build items array with parsed amounts
      const items = invoiceData.items
        .filter(item => item.description && item.amount)
        .map(item => ({
          description: item.description,
          amount: parseFloat(item.amount) || 0
        }));

      const response = await axios.post(`${API}/auto-invoicing/send-monthly-now`, {
        category: monthlyCategory,
        childIds: selectedChildIds,
        dueDate: invoiceData.dueDate,
        billingMonth: monthlyBillingMonth,
        amount: total,
        description: invoiceData.description || `Invoice for multiple children`,
        items: items,
        applyTax: invoiceData.applyTax,
        taxRate: invoiceData.taxRate,
        taxAmount: taxAmount,
        // Discount fields
        discountType: invoiceData.applyDiscount ? invoiceData.discountType : null,
        discountPercent: invoiceData.applyDiscount ? (parseFloat(invoiceData.discountPercent) || 0) : 0,
        discountAmount: invoiceData.applyDiscount ? (parseFloat(invoiceData.discountAmount) || 0) : 0,
        discountReason: invoiceData.discountReason || '',
        ccDirector: autoSettings.ccDirector
      });

      toast.success(`Successfully sent ${response.data.totalCreated} invoices!`);
      
      if (response.data.totalFailed > 0) {
        toast.warning(`${response.data.totalFailed} invoices failed to send`);
      }

      // Reset form
      setInvoiceData(prev => ({
        ...prev,
        items: [{ description: 'Monthly Tuition', amount: '' }],
        description: '',
        applyDiscount: false,
        discountPercent: 0,
        discountAmount: 0,
        discountReason: ''
      }));

      // Refresh data
      fetchHistory();
      fetchMonthlyInvoices();
      
    } catch (error) {
      console.error('Error sending monthly invoices:', error);
      toast.error('Failed to send monthly invoices');
    } finally {
      setSendingMonthly(false);
      setMonthlyProgress({ current: 0, total: 0 });
    }
  };

  const handleDeleteMonthlyInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`${API}/auto-invoicing/invoice/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchMonthlyInvoices();
      fetchHistory();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    if (!window.confirm(`Mark invoice ${invoice.invoiceNumber} as PAID?\n\nThis will:\n• Update the invoice status to PAID\n• Send a payment receipt email to ${invoice.customerEmail}\n• Send a payment notification to you (director)`)) {
      return;
    }

    try {
      const response = await axios.put(`${API}/invoices/${invoice.id}/mark-paid`);
      
      if (response.data.customerEmailSent) {
        toast.success(`Invoice marked as paid! Receipt sent to ${invoice.customerEmail}`);
      } else {
        toast.success('Invoice marked as paid!');
      }
      
      // Refresh the data
      fetchHistory();
      fetchMonthlyInvoices();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    }
  };

  const handleResendReceipt = async (invoice) => {
    if (!invoice.paid) {
      toast.error('Invoice must be marked as paid first');
      return;
    }

    try {
      // Use the resend endpoint to send the receipt again
      await axios.post(`${API}/invoices/${invoice.id}/resend`);
      toast.success(`Receipt resent to ${invoice.customerEmail}`);
    } catch (error) {
      console.error('Error resending receipt:', error);
      toast.error('Failed to resend receipt');
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleViewSavedReport = async (logId, mode = 'view') => {
    try {
      const res = await axios.get(`${API}/auto-invoicing/history/${logId}/report`);
      const html = res.data.html || '';

      if (mode === 'print') {
        // Hidden sandboxed iframe: no allow-scripts / no allow-same-origin => scripts cannot run.
        // allow-modals is included ONLY so the browser print dialog can open.
        const frame = document.createElement('iframe');
        frame.setAttribute('sandbox', 'allow-modals');
        frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        frame.srcdoc = html;
        frame.onload = () => {
          // Wait until content (and images) have rendered before printing.
          setTimeout(() => {
            try {
              frame.contentWindow.focus();
              frame.contentWindow.print();
            } catch (e) {
              toast.error('Unable to open print dialog for this report');
            }
            setTimeout(() => document.body.removeChild(frame), 1000);
          }, 350);
        };
        document.body.appendChild(frame);
      } else {
        setReportViewer({ open: true, html });
      }
    } catch (error) {
      toast.error('Saved report snapshot is not available for this record');
    }
  };

  const handlePrintInvoice = (invoice) => {
    // Create a printable invoice window
    const printWindow = window.open('', '_blank');
    
    const taxLine = invoice.applyTax && invoice.taxAmount > 0 
      ? `<tr><td>Sales Tax (${invoice.taxRate}%)</td><td style="text-align:right">$${invoice.taxAmount?.toFixed(2)}</td></tr>`
      : '';
    
    const zelleInfo = invoice.zelleEmail || invoice.zellePhone 
      ? `<div style="margin-top:20px;padding:15px;background:#DBEAFE;border-radius:8px;">
          <h3 style="margin:0 0 10px 0;color:#1E40AF;">Payment via Zelle</h3>
          ${invoice.zelleEmail ? `<p><strong>Email:</strong> ${invoice.zelleEmail}</p>` : ''}
          ${invoice.zellePhone ? `<p><strong>Phone:</strong> ${invoice.zellePhone}</p>` : ''}
          <p style="font-size:12px;color:#6B7280;">Please include Invoice #${invoice.invoiceNumber} in the memo.</p>
        </div>`
      : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .logo-header { margin-bottom: 8px; }
          .logo-header img { height: 50px; width: auto; }
          .center-name { text-align: center; font-size: 22px; font-weight: bold; color: #1F2937; margin-bottom: 15px; }
          h1 { color: #92400E; margin-bottom: 5px; }
          .header { margin-bottom: 30px; text-align: center; }
          .info-row { display: flex; margin-bottom: 8px; }
          .info-label { width: 150px; color: #6B7280; }
          .info-value { font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #E5E7EB; }
          th { background: #F3F4F6; }
          .total-row { background: #D1FAE5; font-weight: bold; color: #059669; }
          .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; }
          .status-paid { background: #D1FAE5; color: #059669; }
          .status-pending { background: #FEF3C7; color: #92400E; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="logo-header"><img src="/simple-sign-in-logo.png" alt="Logo" onerror="this.style.display='none'" /></div>
        <div class="center-name">${organization?.name || ''}</div>
        <div class="header">
          <h1>INVOICE</h1>
        </div>
        
        <div class="info-row"><span class="info-label">Invoice Number:</span><span class="info-value">${invoice.invoiceNumber}</span></div>
        <div class="info-row"><span class="info-label">Date:</span><span class="info-value">${new Date(invoice.createdAt).toLocaleDateString()}</span></div>
        <div class="info-row"><span class="info-label">Due Date:</span><span class="info-value">${invoice.dueDate || 'N/A'}</span></div>
        <div class="info-row"><span class="info-label">Status:</span><span class="status ${invoice.paid ? 'status-paid' : 'status-pending'}">${invoice.paid ? 'PAID' : 'PENDING'}</span></div>
        
        <div style="margin-top:20px;">
          <h3>Bill To:</h3>
          <p><strong>${invoice.customerName}</strong></p>
          <p>${invoice.customerEmail}</p>
          ${invoice.childName ? `<p>Child: ${invoice.childName}</p>` : ''}
        </div>
        
        <table>
          <thead>
            <tr><th>Description</th><th style="text-align:right">Amount</th></tr>
          </thead>
          <tbody>
            <tr><td>${invoice.description || 'Monthly Childcare Tuition'}</td><td style="text-align:right">$${(invoice.subtotal || invoice.amount)?.toFixed(2)}</td></tr>
            ${taxLine}
            <tr class="total-row"><td>TOTAL DUE</td><td style="text-align:right">$${invoice.amount?.toFixed(2)}</td></tr>
          </tbody>
        </table>
        
        ${zelleInfo}
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const toggleChildSelection = (childId) => {
    setSelectedChildIds(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const selectAllChildren = () => {
    setSelectedChildIds(categoryChildren.filter(c => c.hasBillingEmail).map(c => c.id));
  };

  const deselectAllChildren = () => {
    setSelectedChildIds([]);
  };

  const handleChildSelect = (childId) => {
    const child = children.find(c => c.id === childId);
    if (child) {
      setSelectedChild(child);
      // Get parent email from pickup persons if available
      const parentEmail = child.pickupPersons?.find(p => p.email)?.email || '';
      setInvoiceData(prev => ({
        ...prev,
        childId: child.id,
        childName: child.name,
        parentEmail: parentEmail
      }));
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceData.childId || !invoiceData.parentEmail || calculateTotal() === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      // Calculate totals
      const subtotal = calculateSubtotal();
      const discount = calculateDiscount();
      const taxAmount = calculateTax();
      const total = calculateTotal();
      
      // Build items array with parsed amounts
      const items = invoiceData.items
        .filter(item => item.description && item.amount)
        .map(item => ({
          description: item.description,
          amount: parseFloat(item.amount) || 0
        }));
      
      // Create and send the invoice in one call
      const invoicePayload = {
        childId: invoiceData.childId,
        customerName: invoiceData.childName,
        customerEmail: invoiceData.parentEmail,
        amount: total,
        description: invoiceData.description || `Tuition for ${invoiceData.childName}`,
        dueDate: invoiceData.dueDate,
        items: items,
        applyTax: invoiceData.applyTax,
        taxRate: invoiceData.taxRate,
        taxAmount: taxAmount,
        // Discount fields
        discountType: invoiceData.applyDiscount ? invoiceData.discountType : null,
        discountPercent: invoiceData.applyDiscount ? (parseFloat(invoiceData.discountPercent) || 0) : 0,
        discountAmount: invoiceData.applyDiscount ? (parseFloat(invoiceData.discountAmount) || 0) : 0,
        discountReason: invoiceData.discountReason || ''
      };

      const response = await axios.post(`${API}/invoices/create-and-send`, invoicePayload);
      toast.success(`Invoice created and sent to ${invoiceData.parentEmail}`);
      
      // Reset form
      setInvoiceData({
        childId: '',
        childName: '',
        parentEmail: '',
        amount: '',
        description: '',
        dueDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
        items: [{ description: 'Monthly Tuition', amount: '' }],
        applyTax: false,
        taxRate: 6,
        applyDiscount: false,
        discountType: 'percentage',
        discountPercent: 0,
        discountAmount: 0,
        discountReason: ''
      });
      setSelectedChild(null);
      setCreateDialogOpen(false);
      fetchHistory();
      
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    } finally {
      setSending(false);
    }
  };

  const handleSendInvoiceToParent = async (invoice) => {
    setSending(true);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/send`, {
        ccDirector: autoSettings.ccDirector,
        directorEmail: autoSettings.directorEmail
      });
      toast.success(`Invoice sent to ${invoice.customerEmail}`);
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handleSaveAutoSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auto-invoicing/settings`, {
        ...autoSettings,
        ownerEmail: autoSettings.directorEmail
      });
      toast.success('Auto-invoicing settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewSummary = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/auto-invoicing/preview`, {
        startDate: dateRange.start,
        endDate: dateRange.end,
        recipientEmail: summarySettings.recipientEmail,
        ccEmails: summarySettings.ccEmails,
        ...summarySettings
      });
      setPreviewHtml(response.data.html);
      setPreviewStats(response.data.stats);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setSending(false);
    }
  };

  const handleSendSummary = async () => {
    if (!summarySettings.recipientEmail) {
      toast.error('Please enter director email');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/auto-invoicing/send`, {
        startDate: dateRange.start,
        endDate: dateRange.end,
        recipientEmail: summarySettings.recipientEmail,
        ccEmails: summarySettings.ccEmails.filter(e => e),
        ...summarySettings
      });
      toast.success('Summary sent to director');
      fetchHistory();
      setPreviewHtml('');
      setPreviewStats(null);
    } catch (error) {
      console.error('Error sending summary:', error);
      toast.error('Failed to send summary');
    } finally {
      setSending(false);
    }
  };

  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', amount: '' }]
    }));
  };

  const updateItem = (index, field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (index) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateSubtotal = () => {
    return invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateDiscount = () => {
    if (!invoiceData.applyDiscount) return 0;
    const subtotal = calculateSubtotal();
    let totalDiscount = 0;
    
    if (invoiceData.discountType === 'percentage' || invoiceData.discountType === 'both') {
      totalDiscount += subtotal * ((parseFloat(invoiceData.discountPercent) || 0) / 100);
    }
    
    if (invoiceData.discountType === 'fixed' || invoiceData.discountType === 'both') {
      totalDiscount += parseFloat(invoiceData.discountAmount) || 0;
    }
    
    // Don't allow discount to exceed subtotal
    return Math.min(totalDiscount, subtotal);
  };

  const calculateTax = () => {
    if (!invoiceData.applyTax) return 0;
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const taxableAmount = subtotal - discount;
    return taxableAmount * (invoiceData.taxRate / 100);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    return subtotal - discount + tax;
  };

  const addCcEmail = () => {
    if (newCcEmail && !summarySettings.ccEmails.includes(newCcEmail)) {
      setSummarySettings(prev => ({
        ...prev,
        ccEmails: [...prev.ccEmails, newCcEmail]
      }));
      setNewCcEmail('');
    }
  };

  const removeCcEmail = (email) => {
    setSummarySettings(prev => ({
      ...prev,
      ccEmails: prev.ccEmails.filter(e => e !== email)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Instructions Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5" />
          Invoicing Guide
        </h3>
        <ul className="text-sm text-blue-700 space-y-1 ml-7 list-disc">
          <li><strong>Single Invoice:</strong> Use the <span className="font-semibold text-blue-900">"Payments"</span> tab to create individual invoices with custom amounts and Zelle/Card options</li>
          <li><strong>Bulk Auto Send:</strong> Use <span className="font-semibold text-green-700">"Create & Auto Send"</span> to send invoices to multiple parents using your auto settings</li>
          <li><strong>Auto Monthly Settings:</strong> Configure default amounts, tax, Zelle/Card payment options for auto invoicing</li>
        </ul>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 gap-2 bg-white/50 p-2 rounded-xl mb-4">
          <TabsTrigger value="bulk" className="rounded-lg data-[state=active]:bg-green-500 data-[state=active]:text-white text-xs">
            <Users className="w-4 h-4 mr-1" />
            Create & Auto Send
          </TabsTrigger>
          <TabsTrigger value="auto" className="rounded-lg data-[state=active]:bg-purple-500 data-[state=active]:text-white text-xs">
            <Repeat className="w-4 h-4 mr-1" />
            Auto Monthly Settings
          </TabsTrigger>
          <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-blue-500 data-[state=active]:text-white text-xs">
            <FileText className="w-4 h-4 mr-1" />
            Invoice Summary
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white text-xs">
            <History className="w-4 h-4 mr-1" />
            History
          </TabsTrigger>
        </TabsList>

        {/* BULK SEND TO MULTIPLE PARENTS TAB */}
        <TabsContent value="bulk">
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200">
            <CardHeader>
              <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                <Send className="w-5 h-5" />
                Create & Auto Send Invoices to Parents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                Create and send invoices to multiple parents at once. Set the amount, tax, and discounts below.
                Zelle/Card payment options are configured in Auto Monthly Settings.
              </p>

              {/* Invoice Items */}
              <div className="space-y-3">
                <Label className="text-green-800 font-semibold">Invoice Items</Label>
                {invoiceData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      placeholder="Description (e.g., Monthly Tuition)"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="flex-1 border-green-200"
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => updateItem(index, 'amount', e.target.value)}
                      className="w-32 border-green-200"
                    />
                    {invoiceData.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} className="text-green-600">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>

              {/* Tax Toggle */}
              <div className="bg-amber-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-amber-800 font-semibold">Apply Sales Tax</Label>
                    <p className="text-xs text-amber-600">Add state/local tax to invoice</p>
                  </div>
                  <Switch
                    checked={invoiceData.applyTax}
                    onCheckedChange={(checked) => setInvoiceData(prev => ({ ...prev, applyTax: checked }))}
                  />
                </div>
                
                {invoiceData.applyTax && (
                  <div className="flex items-center gap-2">
                    <Label className="text-amber-800 text-sm whitespace-nowrap">Tax Rate:</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={invoiceData.taxRate}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                      className="w-20 border-amber-200"
                    />
                    <span className="text-amber-800 font-semibold">%</span>
                  </div>
                )}
              </div>

              {/* Discount Section */}
              <div className="bg-pink-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-pink-800 font-semibold">Apply Discount</Label>
                    <p className="text-xs text-pink-600">Subtract discount from invoice total</p>
                  </div>
                  <Switch
                    checked={invoiceData.applyDiscount}
                    onCheckedChange={(checked) => setInvoiceData(prev => ({ ...prev, applyDiscount: checked }))}
                  />
                </div>
                
                {invoiceData.applyDiscount && (
                  <div className="space-y-3">
                    <Select
                      value={invoiceData.discountType}
                      onValueChange={(value) => setInvoiceData(prev => ({ ...prev, discountType: value }))}
                    >
                      <SelectTrigger className="border-pink-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Discount</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                        <SelectItem value="both">Both (% then fixed)</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {(invoiceData.discountType === 'percentage' || invoiceData.discountType === 'both') && (
                      <div className="flex items-center gap-2">
                        <Label className="text-pink-800 text-sm whitespace-nowrap">Discount %:</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={invoiceData.discountPercent}
                          onChange={(e) => setInvoiceData(prev => ({ ...prev, discountPercent: parseFloat(e.target.value) || 0 }))}
                          className="w-20 border-pink-200"
                        />
                        <span className="text-pink-800 font-semibold">%</span>
                      </div>
                    )}
                    
                    {(invoiceData.discountType === 'fixed' || invoiceData.discountType === 'both') && (
                      <div className="flex items-center gap-2">
                        <Label className="text-pink-800 text-sm whitespace-nowrap">Discount $:</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={invoiceData.discountAmount}
                          onChange={(e) => setInvoiceData(prev => ({ ...prev, discountAmount: parseFloat(e.target.value) || 0 }))}
                          className="w-24 border-pink-200"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-pink-800 text-sm">Discount Reason (optional)</Label>
                      <Input
                        value={invoiceData.discountReason}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, discountReason: e.target.value }))}
                        placeholder="e.g., Sibling discount, Early payment"
                        className="border-pink-200 mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Due Date */}
              <div>
                <Label className="text-green-800 font-semibold">Due Date</Label>
                <Input
                  type="date"
                  value={invoiceData.dueDate}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="border-green-200 mt-1"
                />
              </div>

              {/* Billing Month (separate from due date) */}
              <div>
                <Label className="text-green-800 font-semibold">Billing Month</Label>
                <Input
                  type="month"
                  value={monthlyBillingMonth}
                  onChange={(e) => setMonthlyBillingMonth(e.target.value)}
                  className="border-green-200 mt-1"
                  data-testid="monthly-billing-month"
                />
                <p className="text-xs text-gray-500 mt-1">The month this invoice covers. Prevents billing the same child twice for one month.</p>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-green-800 font-semibold">Notes (Optional)</Label>
                <Textarea
                  value={invoiceData.description}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Any additional notes for the parent..."
                  className="border-green-200 mt-1"
                  rows={2}
                />
              </div>

              {/* Category Selection for Multiple Children */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <Label className="text-blue-800 font-semibold">Select Children to Send Invoice</Label>
                <p className="text-xs text-blue-600 mb-2">Select multiple children by category to send the same invoice to all their parents</p>
                
                <Select
                  value={monthlyCategory}
                  onValueChange={setMonthlyCategory}
                >
                  <SelectTrigger className="border-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories (Full Time, Part Time, After School)</SelectItem>
                    <SelectItem value="full_time">Full Time Only</SelectItem>
                    <SelectItem value="part_time">Part Time Only</SelectItem>
                    <SelectItem value="after_school">After School Only</SelectItem>
                  </SelectContent>
                </Select>

                {/* Children Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-blue-800 font-semibold">
                      Select Children ({selectedChildIds.length} of {categoryChildren.filter(c => c.hasBillingEmail).length} selected)
                    </Label>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllChildren} className="text-xs">
                        Select All
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllChildren} className="text-xs">
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  
                  {loadingCategoryChildren ? (
                    <div className="text-center py-4 text-gray-500">Loading children...</div>
                  ) : categoryChildren.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                      No children found in this category
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-blue-200 rounded-lg p-2 space-y-1">
                      {categoryChildren.map(child => (
                        <div 
                          key={child.id}
                          className={`p-2 rounded border transition-colors ${
                            selectedChildIds.includes(child.id) 
                              ? 'bg-blue-100 border-blue-300' 
                              : 'bg-white border-transparent'
                          }`}
                          data-testid={`auto-child-row-${child.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedChildIds.includes(child.id)}
                                onChange={() => child.hasBillingEmail && toggleChildSelection(child.id)}
                                disabled={!child.hasBillingEmail}
                                className="w-4 h-4 text-blue-600"
                                data-testid={`auto-child-checkbox-${child.id}`}
                              />
                              <div>
                                <span className="font-medium text-gray-800">{child.name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({child.category?.replace('_', ' ') || 'N/A'})
                                </span>
                                <div className="text-xs">
                                  {child.hasBillingEmail ? (
                                    <span className="text-gray-500">
                                      {child.billingContactName ? `${child.billingContactName} · ` : ''}{child.billingContactEmail}
                                      {child.textBillingContact && child.billingContactMobile ? ' · SMS on' : ''}
                                    </span>
                                  ) : (
                                    <span className="text-red-500 font-medium">Billing Contact Required</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => (editingBillingId === child.id ? setEditingBillingId(null) : openBillingEditor(child))}
                              data-testid={`edit-billing-${child.id}`}
                            >
                              {editingBillingId === child.id ? 'Close' : (child.hasBillingEmail ? 'Edit Billing' : 'Add Billing')}
                            </Button>
                          </div>

                          {editingBillingId === child.id && (
                            <div className="mt-2 p-3 rounded-lg border border-blue-200 bg-blue-50 space-y-2" data-testid={`billing-editor-${child.id}`}>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs text-blue-800">Billing Contact Name</Label>
                                  <Input
                                    value={billingForm.billingContactName}
                                    onChange={(e) => setBillingForm({ ...billingForm, billingContactName: e.target.value })}
                                    placeholder="Jane Doe"
                                    data-testid={`billing-name-${child.id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-blue-800">Billing Email</Label>
                                  <Input
                                    type="email"
                                    value={billingForm.billingContactEmail}
                                    onChange={(e) => setBillingForm({ ...billingForm, billingContactEmail: e.target.value })}
                                    placeholder="billing@example.com"
                                    data-testid={`billing-email-${child.id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-blue-800">Billing Mobile</Label>
                                  <Input
                                    value={billingForm.billingContactMobile}
                                    onChange={(e) => setBillingForm({ ...billingForm, billingContactMobile: e.target.value })}
                                    placeholder="(555) 123-4567"
                                    data-testid={`billing-mobile-${child.id}`}
                                  />
                                </div>
                                <div className="flex items-center justify-between pt-5">
                                  <Label className="text-xs text-blue-800">Text Billing Contact</Label>
                                  <Switch
                                    checked={billingForm.textBillingContact}
                                    onCheckedChange={(checked) => setBillingForm({ ...billingForm, textBillingContact: checked })}
                                    data-testid={`billing-text-toggle-${child.id}`}
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingBillingId(null)}>Cancel</Button>
                                <Button type="button" size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => saveBillingContact(child.id)} data-testid={`save-billing-${child.id}`}>Save</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {categoryChildren.filter(c => !c.hasBillingEmail).length > 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ {categoryChildren.filter(c => !c.hasBillingEmail).length} child(ren) need a Billing Contact before they can receive auto invoices.
                    </p>
                  )}
                </div>
              </div>

              {/* Total Display */}
              <div className="bg-green-100 p-4 rounded-lg border-2 border-green-300">
                <div className="text-center">
                  <p className="text-sm text-green-700">Invoice Total (per child)</p>
                  <p className="text-3xl font-bold text-green-800">
                    ${calculateTotal().toFixed(2)}
                  </p>
                  {invoiceData.applyTax && (
                    <p className="text-xs text-green-600">
                      (includes {invoiceData.taxRate}% tax)
                    </p>
                  )}
                  {invoiceData.applyDiscount && calculateDiscount() > 0 && (
                    <p className="text-xs text-pink-600">
                      Discount: -${calculateDiscount().toFixed(2)}
                      {invoiceData.discountReason && ` (${invoiceData.discountReason})`}
                    </p>
                  )}
                  {selectedChildIds.length > 1 && (
                    <p className="text-sm text-green-700 mt-2">
                      Total for {selectedChildIds.length} children: <strong>${(calculateTotal() * selectedChildIds.length).toFixed(2)}</strong>
                    </p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSendMonthlyNow}
                disabled={sendingMonthly || selectedChildIds.length === 0 || calculateTotal() === 0}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingMonthly 
                  ? `Sending ${monthlyProgress.current}/${monthlyProgress.total}...` 
                  : `Create & Send ${selectedChildIds.length} Invoice${selectedChildIds.length !== 1 ? 's' : ''}`}
              </Button>

              {calculateTotal() === 0 && (
                <p className="text-xs text-amber-600 text-center">
                  ⚠️ Add at least one invoice item with an amount
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUTO MONTHLY SETTINGS TAB */}
        <TabsContent value="auto">
          <div className="space-y-6">
            {/* Settings Card */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-purple-200">
              <CardHeader>
                <CardTitle className="text-xl text-purple-800 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Auto Monthly Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-gray-600 bg-purple-50 p-3 rounded-lg">
                  Configure automatic monthly invoices. When enabled, invoices will be created and sent on the selected day each month.
                </p>

                {/* Enable Auto Invoicing */}
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div>
                    <Label className="text-purple-800 font-semibold">Enable Auto Monthly Invoicing</Label>
                    <p className="text-sm text-purple-600">Automatically create and send invoices each month</p>
                  </div>
                  <Switch
                    checked={autoSettings.enabled}
                    onCheckedChange={(checked) => setAutoSettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Day of Month */}
                  <div>
                    <Label className="text-purple-800 font-semibold">Send Invoices On</Label>
                    <Select
                      value={String(autoSettings.dayOfMonth || 1)}
                      onValueChange={(value) => setAutoSettings(prev => ({ ...prev, dayOfMonth: parseInt(value) }))}
                    >
                      <SelectTrigger className="border-purple-200 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={String(day)}>
                            {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Default Amount */}
                  <div>
                    <Label className="text-purple-800 font-semibold">Default Monthly Amount ($)</Label>
                    <Input
                      type="number"
                      value={autoSettings.defaultAmount}
                      onChange={(e) => setAutoSettings(prev => ({ ...prev, defaultAmount: e.target.value }))}
                      placeholder="Enter default amount"
                      className="border-purple-200 mt-1"
                    />
                  </div>
                </div>

                {/* Default Description */}
                <div>
                  <Label className="text-purple-800 font-semibold">Default Invoice Description</Label>
                  <Input
                    value={autoSettings.defaultDescription}
                    onChange={(e) => setAutoSettings(prev => ({ ...prev, defaultDescription: e.target.value }))}
                    placeholder="Monthly Childcare Tuition"
                    className="border-purple-200 mt-1"
                  />
                </div>

                {/* Tax Settings */}
                <div className="bg-amber-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-amber-800 font-semibold">Apply Sales Tax</Label>
                      <p className="text-xs text-amber-600">Automatically apply tax to monthly invoices</p>
                    </div>
                    <Switch
                      checked={autoSettings.applyTax}
                      onCheckedChange={(checked) => setAutoSettings(prev => ({ ...prev, applyTax: checked }))}
                    />
                  </div>
                  
                  {autoSettings.applyTax && (
                    <div className="flex items-center gap-2">
                      <Label className="text-amber-800 text-sm whitespace-nowrap">Tax Rate:</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="20"
                        value={autoSettings.taxRate}
                        onChange={(e) => setAutoSettings(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                        className="w-20 border-amber-200"
                      />
                      <span className="text-amber-800 font-semibold">%</span>
                    </div>
                  )}
                </div>

                {/* Zelle Payment Settings */}
                <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                  <Label className="text-blue-800 font-semibold block mb-2">Zelle Payment Destination</Label>
                  <p className="text-xs text-blue-600 mb-3">
                    Parents will send payments to this Zelle account. Enter either email OR phone number.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-700 text-sm">Zelle Email</Label>
                      <Input
                        type="email"
                        value={autoSettings.zelleEmail || ''}
                        onChange={(e) => setAutoSettings(prev => ({ ...prev, zelleEmail: e.target.value }))}
                        placeholder="zelle@email.com"
                        className="border-blue-200 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-700 text-sm">Zelle Phone</Label>
                      <Input
                        type="tel"
                        value={autoSettings.zellePhone || ''}
                        onChange={(e) => setAutoSettings(prev => ({ ...prev, zellePhone: e.target.value }))}
                        placeholder="(412) 555-1234"
                        className="border-blue-200 mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Square Card Payment Settings */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg space-y-3 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-emerald-800 font-semibold block">Square Card Payments</Label>
                      <p className="text-xs text-emerald-600 mt-1">
                        Accept credit/debit card payments from parents (2.9% + $0.30 + $5 platform fee passed to parent)
                      </p>
                    </div>
                    {squareStatus.connected ? (
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Connected
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">
                        Not Connected
                      </span>
                    )}
                  </div>
                  
                  {squareStatus.connected ? (
                    <div className="bg-white/70 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-800">
                            {squareStatus.business_name || 'Square Account'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Connected {squareStatus.connected_at ? new Date(squareStatus.connected_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisconnectSquare}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Disconnect
                        </Button>
                      </div>
                      <p className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded">
                        Parents will see a "Pay with Card" option on invoices. Payments go directly to your Square account.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-white/70 rounded-lg p-3">
                        <p className="text-sm text-gray-700 mb-2">
                          Connect your Square account to accept card payments from parents.
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 mb-3">
                          <li>• Parents pay with Credit Card, Debit Card, Apple Pay, Google Pay</li>
                          <li>• Payments go directly to YOUR Square account</li>
                          <li>• Invoices auto-update to "PAID" when payment completes</li>
                          <li>• Processing fee: 2.9% + $0.30 (Square) + $5 platform fee</li>
                        </ul>
                        <Button
                          onClick={handleConnectSquare}
                          disabled={connectingSquare}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {connectingSquare ? 'Connecting...' : 'Connect Square Account'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleSaveAutoSettings}
                  disabled={loading}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                >
                  {loading ? 'Saving...' : 'Save Auto-Invoicing Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Monthly Invoices History */}
            {monthlyInvoices.length > 0 && (
              <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl text-amber-800 flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Auto-Generated Invoices ({monthlyInvoices.length})
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.print()}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print Summary
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {monthlyInvoices.map(invoice => (
                      <div 
                        key={invoice.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          invoice.paid 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div>
                          <p className={`font-semibold ${invoice.paid ? 'text-green-800' : 'text-amber-800'}`}>
                            {invoice.childName || invoice.customerName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {invoice.invoiceNumber} • {invoice.customerEmail}
                          </p>
                          <p className="text-xs text-gray-400">
                            Created: {new Date(invoice.createdAt).toLocaleDateString()}
                            {invoice.paid && invoice.paidDate && (
                              <span className="ml-2 text-green-600">
                                • Paid: {new Date(invoice.paidDate).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="text-right mr-2">
                            <p className={`font-bold ${invoice.paid ? 'text-green-700' : 'text-amber-700'}`}>
                              ${invoice.amount?.toFixed(2)}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              invoice.paid ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                            }`}>
                              {invoice.paid ? 'PAID' : 'PENDING'}
                            </span>
                          </div>
                          
                          {/* Mark as Paid or Resend Receipt */}
                          {!invoice.paid ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAsPaid(invoice)}
                              className="text-green-500 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                              title="Mark as Paid (sends receipt)"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendReceipt(invoice)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                              title="Resend Receipt"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoicePdf(invoice.id, invoice.invoiceNumber)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintInvoice(invoice)}
                            className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-8 w-8 p-0"
                            title="Print Invoice"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMonthlyInvoice(invoice.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            title="Delete Invoice"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* DIRECTOR SUMMARY TAB */}
        <TabsContent value="summary">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-xl text-blue-800 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Invoice Summary Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  Generate a summary of invoices for a selected date range. Use this report for accounting, bookkeeping, or internal review.
                </p>

                {/* Date Range */}
                <div className="space-y-2">
                  <Label className="text-blue-800 font-semibold">Date Range</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm text-gray-600">Start Date</Label>
                      <Input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        className="border-blue-200"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">End Date</Label>
                      <Input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        className="border-blue-200"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => {
                        const lastMonth = subMonths(new Date(), 1);
                        setDateRange({
                          start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
                          end: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Last Month
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => {
                        setDateRange({
                          start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
                          end: format(new Date(), 'yyyy-MM-dd')
                        });
                      }}
                    >
                      This Month
                    </Button>
                  </div>
                </div>

                {/* Director Email */}
                <div>
                  <Label className="text-blue-800 font-semibold">Send Report To</Label>
                  <Input
                    type="email"
                    value={summarySettings.recipientEmail}
                    onChange={(e) => setSummarySettings(prev => ({ ...prev, recipientEmail: e.target.value }))}
                    placeholder="director@example.com"
                    className="border-blue-200 mt-1"
                  />
                </div>

                {/* CC Recipients */}
                <div>
                  <Label className="text-blue-800 font-semibold">CC Additional Recipient</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="email"
                      value={newCcEmail}
                      onChange={(e) => setNewCcEmail(e.target.value)}
                      placeholder="accountant@example.com"
                      className="border-blue-200"
                      onKeyPress={(e) => e.key === 'Enter' && addCcEmail()}
                    />
                    <Button onClick={addCcEmail} size="sm" variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {summarySettings.ccEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {summarySettings.ccEmails.map((email, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                          {email}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeCcEmail(email)} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sections to Include */}
                <div className="space-y-3 pt-4 border-t border-blue-200">
                  <Label className="text-blue-800 font-semibold">Include in Summary</Label>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Total Invoices Sent</Label>
                    <Switch
                      checked={summarySettings.includeTotalInvoices}
                      onCheckedChange={(checked) => setSummarySettings(prev => ({ ...prev, includeTotalInvoices: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Total Amount Billed</Label>
                    <Switch
                      checked={summarySettings.includeTotalBilled}
                      onCheckedChange={(checked) => setSummarySettings(prev => ({ ...prev, includeTotalBilled: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Breakdown by Family</Label>
                    <Switch
                      checked={summarySettings.includeBreakdownByFamily}
                      onCheckedChange={(checked) => setSummarySettings(prev => ({ ...prev, includeBreakdownByFamily: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Outstanding Balances</Label>
                    <Switch
                      checked={summarySettings.includeOutstandingBalances}
                      onCheckedChange={(checked) => setSummarySettings(prev => ({ ...prev, includeOutstandingBalances: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Payment Summary</Label>
                    <Switch
                      checked={summarySettings.includePaymentSummary}
                      onCheckedChange={(checked) => setSummarySettings(prev => ({ ...prev, includePaymentSummary: checked }))}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handlePreviewSummary}
                    disabled={sending}
                    variant="outline"
                    className="flex-1 border-blue-400 text-blue-700"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    onClick={handleSendSummary}
                    disabled={sending || !summarySettings.recipientEmail}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Sending...' : 'Send Report'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preview Panel */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                {previewStats && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-amber-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">{previewStats.totalInvoices}</p>
                      <p className="text-xs text-amber-700">Total Invoices</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">${previewStats.totalBilled?.toFixed(2)}</p>
                      <p className="text-xs text-blue-700">Total Billed</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">${previewStats.totalPaid?.toFixed(2)}</p>
                      <p className="text-xs text-green-700">Paid</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">${previewStats.totalOutstanding?.toFixed(2)}</p>
                      <p className="text-xs text-red-700">Outstanding</p>
                    </div>
                  </div>
                )}
                
                {previewHtml ? (
                  <div 
                    className="bg-white rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Click "Preview" to see the summary</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history">
          <div className="space-y-6">
            {/* Sent Invoices Section */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Sent Invoices ({sentInvoices.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sentInvoices.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No invoices sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {sentInvoices.map((invoice, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border flex justify-between items-center ${
                          invoice.paid 
                            ? 'bg-green-50 border-green-300' 
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div>
                          <p className={`font-semibold ${invoice.paid ? 'text-green-800' : 'text-amber-800'}`}>
                            {invoice.customerName}
                          </p>
                          <p className="text-sm text-gray-600">{invoice.customerEmail}</p>
                          <p className="text-xs text-gray-500">
                            {invoice.invoiceNumber} - {invoice.description}
                            {invoice.paid && invoice.paidDate && (
                              <span className="ml-2 text-green-600">
                                • Paid: {new Date(invoice.paidDate).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="text-right mr-2">
                            <p className={`text-lg font-bold ${invoice.paid ? 'text-green-600' : 'text-amber-600'}`}>
                              ${invoice.amount?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {invoice.sentAt ? new Date(invoice.sentAt).toLocaleDateString() : new Date(invoice.createdAt).toLocaleDateString()}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded-full ${invoice.paid ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
                              {invoice.paid ? 'PAID' : 'PENDING'}
                            </span>
                          </div>
                          
                          {/* Mark as Paid or Resend Receipt */}
                          {!invoice.paid ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAsPaid(invoice)}
                              className="text-green-500 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                              title="Mark as Paid (sends receipt)"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendReceipt(invoice)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                              title="Resend Receipt"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoicePdf(invoice.id, invoice.invoiceNumber)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintInvoice(invoice)}
                            className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-8 w-8 p-0"
                            title="Print Invoice"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Auto Monthly Run History */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Auto Monthly Run History ({history.filter(h => h.type === 'monthly_run').length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.filter(h => h.type === 'monthly_run').length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No auto monthly runs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.filter(h => h.type === 'monthly_run').map((item, idx) => (
                      <div key={idx} className="bg-green-50 p-4 rounded-lg border border-green-200" data-testid="monthly-run-summary">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-green-800">
                              Monthly invoice run — {item.billingPeriod}
                            </p>
                            <p className="text-sm text-green-700">
                              {item.createdCount} sent · {item.failedCount} failed · {item.skippedCount} skipped
                            </p>
                            {item.failedChildren?.length > 0 && (
                              <p className="text-xs text-red-600 mt-1">
                                Not sent: {item.failedChildren.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(item.sentAt).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">{item.source}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Director Summary Reports */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200">
              <CardHeader>
                <CardTitle className="text-xl text-amber-800 flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Invoice Summary Reports ({history.filter(h => h.type !== 'monthly_run').length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.filter(h => h.type !== 'monthly_run').length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No summary reports sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.filter(h => h.type !== 'monthly_run').map((item, idx) => (
                      <div key={idx} className="bg-amber-50 p-4 rounded-lg border border-amber-200" data-testid="director-summary-report">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-amber-800">
                              {item.dateRange?.start} to {item.dateRange?.end}
                            </p>
                            <p className="text-sm text-amber-600">
                              Sent to: {item.recipients?.join(', ')}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {item.invoiceCount} invoice(s) in report
                            </p>
                            {item.hasSnapshot && (
                              <div className="flex gap-2 mt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-amber-700 border-amber-300"
                                  onClick={() => handleViewSavedReport(item.id, 'view')}
                                  data-testid="view-saved-report-btn"
                                >
                                  <Eye className="w-4 h-4 mr-1" /> View Report
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-gray-700 border-gray-300"
                                  onClick={() => handleViewSavedReport(item.id, 'print')}
                                  data-testid="print-saved-report-btn"
                                >
                                  <Printer className="w-4 h-4 mr-1" /> Print
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(item.sentAt).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              by {item.sentBy}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Read-only saved-report viewer (sandboxed: no scripts, opaque origin) */}
            <Dialog
              open={reportViewer.open}
              onOpenChange={(open) => setReportViewer((v) => ({ ...v, open }))}
            >
              <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col" data-testid="saved-report-viewer">
                <DialogHeader className="p-4 pb-2">
                  <DialogTitle className="text-amber-800">Saved Invoice Summary Report</DialogTitle>
                </DialogHeader>
                <iframe
                  title="Saved Invoice Summary Report"
                  sandbox=""
                  srcDoc={reportViewer.html}
                  className="w-full flex-1 border-0"
                  data-testid="saved-report-iframe"
                />
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AutoInvoicingTab;
